import { GatewayAPIClient, gatewayAPIClient } from "../../app/api/client";

export interface SearchMatchPosition {
  start: number;
  length: number;
}

export type SearchHighlightMap = Record<string, SearchMatchPosition[]>;

export interface SearchMessageHit {
  chat_id: string;
  message_id: string;
  sequence: number;
  snippet: string;
  conversation_label?: string;
  highlight?: SearchHighlightMap;
}

export interface SearchMessagesResponse {
  hits: SearchMessageHit[];
  estimated_total_hits: number;
  limit: number;
  offset: number;
}

export interface SearchMessageInput {
  query: string;
  chatID?: string | null;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

export interface SearchContextAttachment {
  media_id: string;
  kind: string;
}

export interface SearchContextMessage {
  message_id: string;
  chat_id: string;
  sequence: number;
  sender_user_id: string;
  body: string;
  root_post_id?: string;
  attachments?: SearchContextAttachment[];
  idempotency_key: string;
  edit_version: number;
  deleted_by_user_id?: string;
  created_at: string;
  updated_at: string;
  edited_at?: string;
  deleted_at?: string;
}

export interface SearchContextResponse {
  chat_id: string;
  around_sequence: number;
  messages: SearchContextMessage[];
}

export interface SearchContextInput {
  chatID: string;
  aroundSequence: number;
  before?: number;
  after?: number;
  signal?: AbortSignal;
}

type SearchAPIClient = Pick<GatewayAPIClient, "request">;

function getClient(client?: SearchAPIClient): SearchAPIClient {
  return client ?? gatewayAPIClient;
}

function buildPath(basePath: string, query: URLSearchParams): string {
  const encoded = query.toString();
  if (encoded === "") {
    return basePath;
  }
  return `${basePath}?${encoded}`;
}

function normalizeIdentifier(value: string, fieldName: string): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error(`${fieldName} must not be empty`);
  }
  if (normalized.includes("/")) {
    throw new Error(`${fieldName} contains invalid path separator`);
  }
  return normalized;
}

function normalizePositiveInteger(
  value: number | undefined,
  fieldName: string,
  allowZero: boolean,
): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!Number.isFinite(value)) {
    throw new Error(`${fieldName} must be a finite number`);
  }
  const normalized = Math.trunc(value);
  if (allowZero) {
    if (normalized < 0) {
      throw new Error(`${fieldName} must be >= 0`);
    }
  } else if (normalized <= 0) {
    throw new Error(`${fieldName} must be > 0`);
  }
  return normalized;
}

export async function searchMessages(
  input: SearchMessageInput,
  client?: SearchAPIClient,
): Promise<SearchMessagesResponse> {
  const query = input.query.trim();
  if (query === "") {
    throw new Error("query must not be empty");
  }

  const limit = normalizePositiveInteger(input.limit, "limit", false);
  const offset = normalizePositiveInteger(input.offset, "offset", true);
  const chatID = input.chatID === undefined || input.chatID === null ? undefined : normalizeIdentifier(input.chatID, "chat_id");

  const params = new URLSearchParams();
  params.set("q", query);
  if (chatID !== undefined) {
    params.set("chat_id", chatID);
  }
  if (limit !== undefined) {
    params.set("limit", String(limit));
  }
  if (offset !== undefined) {
    params.set("offset", String(offset));
  }

  return getClient(client).request<SearchMessagesResponse>({
    method: "GET",
    path: buildPath("/v1/search/messages", params),
    signal: input.signal,
  });
}

export async function fetchMessageContext(
  input: SearchContextInput,
  client?: SearchAPIClient,
): Promise<SearchContextResponse> {
  const chatID = normalizeIdentifier(input.chatID, "chat_id");
  const aroundSequence = normalizePositiveInteger(input.aroundSequence, "around_sequence", false);
  const before = normalizePositiveInteger(input.before, "before", true);
  const after = normalizePositiveInteger(input.after, "after", true);

  const query = new URLSearchParams();
  query.set("around_sequence", String(aroundSequence));
  if (before !== undefined) {
    query.set("before", String(before));
  }
  if (after !== undefined) {
    query.set("after", String(after));
  }

  return getClient(client).request<SearchContextResponse>({
    method: "GET",
    path: buildPath(`/v1/chats/${encodeURIComponent(chatID)}/messages/context`, query),
    signal: input.signal,
  });
}
